import { format } from 'date-fns';

/**
 * Format a video name, truncating if needed
 * @param {string} name - The name to format
 * @param {number} maxLength - Maximum length before truncating
 * @returns {string} Formatted name
 */
export const formatVideoName = (name, maxLength = 35) => {
    if (!name) return 'Unnamed Video';
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
};

/**
 * Format duration string to a consistent format
 * @param {string} duration - Duration string to format
 * @returns {string} Formatted duration
 */
export const formatDuration = (duration) => {
    if (!duration) return '-';

    // If it's already in a good format, just return it
    if (duration.length <= 8) return duration;

    // Try to normalize format
    const parts = duration.split(':');

    if (parts.length === 3) {
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        const seconds = parseInt(parts[2], 10);

        if (hours === 0) {
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        } else {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
    }

    return duration;
};

// Format date from ISO string or timestamp
export const formatDate = (dateString, formatType = 'table') => {
    if (!dateString) return 'Unknown date';
    try {
        const date = new Date(dateString);
        // Format date and time separately
        const formattedDate = format(date, 'MMM d, yyyy');
        const formattedTime = format(date, 'h:mm a');

        // Return format based on requested style
        if (formatType === 'table') {
            // For table cells, use <br> element that will work in JSX
            return (
                <>
                    {formattedDate}
                    <br />
                    {formattedTime}
                </>
            );
        } else {
            // For text fields, use newline character
            return `${formattedDate}\n${formattedTime}`;
        }
    } catch (e) {
        return 'Invalid date';
    }
};

/**
 * Get color scheme for a given access type
 * @param {string} accessType - The type of access (READ, WRITE, ADMIN)
 * @returns {object} Color scheme object with background, border and text colors
 */
export const getColorScheme = (accessType) => {
    switch (accessType) {
        case 'READ':
            // Updated to a blue-based scheme
            return {
                bg: 'rgba(25, 118, 210, 0.1)',
                border: '#1976d2',
                color: '#1565c0',
            };
        case 'WRITE':
            // Updated to a purple-based scheme
            return {
                bg: 'rgba(123, 31, 162, 0.1)',
                border: '#7b1fa2',
                color: '#6a1b9a',
            };
        case 'ADMIN':
            // Updated to a red-based scheme
            return {
                bg: 'rgba(211, 47, 47, 0.1)',
                border: '#d32f2f',
                color: '#c62828',
            };
        default:
            return {
                bg: 'rgba(97, 97, 97, 0.1)',
                border: '#616161',
                color: '#424242',
            };
    }
};

export const validateItemName = (value, itemType = 'folder') => {
    // Check for empty name or whitespace-only name
    if (!value || value.trim() === '') {
        return `Please enter a valid ${itemType} name`;
    }

    /// Updated regex to allow only alphanumeric characters, underscores, and hyphens
    // This means any special character except _ and - will be invalid
    const validNamePattern = /^[a-zA-Z0-9_\s][\w\-\s]*$/;
    if (!validNamePattern.test(value)) {
        return `${
            itemType.charAt(0).toUpperCase() + itemType.slice(1)
        } name must start with a letter, number, or underscore and can only contain letters, numbers, spaces, underscores (_), and hyphens (-)`;
    }

    // Name is valid
    return null;
};

// Format path for display
export const formatPath = (path) => {
    const formattedPath = path === '/' ? 'Home (/)' : path;
    // replace / with > for better readability
    if (formattedPath === '/') return 'Home';

    const formattedPathWithArrow = 'Home' + formattedPath.replace(/\//g, ' > ');
    // Always start with Home
    return formattedPathWithArrow;
};

/**
 * Format time in seconds to HH:MM:SS format
 * @param {number} seconds - Time in seconds
 * @returns {string} - Formatted time string
 */
export const formatTimeFromSeconds = (seconds) => {
    if (seconds === undefined || seconds === null) return '00:00:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return [hours, minutes, secs].map((v) => (v < 10 ? `0${v}` : v)).join(':');
};

/**
 * Formats user email by removing domain (e.g., "@amrutvaani.org")
 * @param {string} email - The email address to format
 * @returns {string} - Formatted user name
 */
export const formatUserName = (email) => {
    if (!email) return 'Unknown';

    // Remove @amrutvaani.org or any other domain
    return email.split('@')[0];
};

export function secondsToTime(seconds) {
    const hours = Math.floor(seconds / 3600)
        .toString()
        .padStart(2, '0');
    const minutes = Math.floor((seconds % 3600) / 60)
        .toString()
        .padStart(2, '0');
    const secs = Math.floor(seconds % 60)
        .toString()
        .padStart(2, '0');
    const millisecs = Math.floor((seconds % 1) * 1000)
        .toString()
        .padStart(3, '0');
    return `${hours}:${minutes}:${secs}.${millisecs}`;
}

export function timeToSeconds(time) {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
}

export function formatSaveType(saveType) {
    switch (saveType) {
        case 'INITIAL_CREATION':
            return 'AI Generated';
        case 'STAGE_TRANSITION':
            return 'Stage Transition';
        case 'IMPORT':
            return 'Imported';
        case 'MANUAL':
            return 'Manual Save';
        case 'AUTO':
            return 'Auto Save';
        default:
            return saveType;
    }
}

export function formatProcessStage(processStage) {
    switch (processStage) {
        case 'INITIAL':
            return 'Initial';
        case 'REVIEW':
            return 'Review';
        case 'FINAL':
            return 'Final';
        case 'ARCHIVED':
            return 'Archived';
        default:
            return processStage;
    }
}

// Format processing time from minutes to readable format (e.g., "1h 30m")
export const formatProcessingTime = (minutes) => {
    if (minutes === null || minutes === undefined) return 'N/A';

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);

    if (hours === 0) {
        return `${remainingMinutes}m`;
    } else if (remainingMinutes === 0) {
        return `${hours}h`;
    } else {
        return `${hours}h ${remainingMinutes}m`;
    }
};

// Add a helper function to format seconds to HH:MM:SS
export const formatTotalDuration = (totalSeconds) => {
    if (!totalSeconds) return 'N/A';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
};

// Add a more human-readable format for duration
export const formatDurationHumanReadable = (totalSeconds) => {
    if (!totalSeconds) return 'N/A';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
        return `${hours} hr${hours !== 1 ? 's' : ''} ${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else {
        return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    }
};
